
export function parseProps(
    str: string,
    context: any = {}
) {

  str = normalizeTemplate(str);

  const props: Record<string, any> = {};

  let i = 0;

  // --------------------------------------------------
  // HELPERS
  // --------------------------------------------------

  const skipSpaces = () => {
    while (
        i < str.length &&
        /\s/.test(str[i])
        ) {
      i++;
    }
  };


  const readQuotedString = () => {

    const quote = str[i];

    i++; // skip opening quote

    let value = "";

    while (i < str.length) {

      const char = str[i];

      // escaped quote
      if (
          char === "\\" &&
          i + 1 < str.length
      ) {
        value += char;
        value += str[i + 1];
        i += 2;
        continue;
      }

      // closing quote
      if (char === quote) {
        i++;
        break;
      }

      value += char;
      i++;
    }

    return value;
  };

  const readExpression = () => {

    let depth = 1;

    i++;

    let value = "";

    let quote: string | null = null;

    while (
        i < str.length &&
        depth > 0
        ) {

      const char = str[i];

      if (quote) {

        if (
            char === quote &&
            str[i - 1] !== "\\"
        ) {
          quote = null;
        }

        value += char;
        i++;
        continue;
      }

      if (
          char === '"' ||
          char === "'" ||
          char === "`"
      ) {

        quote = char;

        value += char;

        i++;

        continue;
      }

      if (char === "{") {
        depth++;
      }

      if (char === "}") {
        depth--;
      }

      if (depth > 0) {
        value += char;
      }

      i++;
    }

    return value;
  };

  // --------------------------------------------------
  // MAIN LOOP
  // --------------------------------------------------

  while (i < str.length) {

    // -----------------------------------
    // SKIP SPACES
    // -----------------------------------

    skipSpaces();

    if (i >= str.length) {
      break;
    }

    // -----------------------------------
    // PARSE KEY
    // -----------------------------------

    let key = "";

    while (
        i < str.length &&
        /[a-zA-Z0-9_.-]/.test(str[i])
        ) {
      key += str[i++];
    }

    // invalid char → skip
    if (!key) {
      i++;
      continue;
    }

    // -----------------------------------
    // SKIP SPACE BEFORE "="
    // -----------------------------------

    skipSpaces();

    // -----------------------------------
    // BOOLEAN PROP
    // -----------------------------------

    if (str[i] !== "=") {

      props[key] = true;

      continue;
    }

    i++; // skip "="

    // -----------------------------------
    // SKIP SPACE AFTER "="
    // -----------------------------------

    skipSpaces();

    // -----------------------------------
    // STRING VALUE
    // -----------------------------------

    if (
        str[i] === '"' ||
        str[i] === "'" ||
        str[i] === "`"
    ) {

      props[key] = readQuotedString();

      continue;
    }

    // -----------------------------------
    // EXPRESSION { ... }
    // -----------------------------------

    if (str[i] === "{") {

      const value = readExpression();

      props[key] = safeEval(
          value,
          context,
          key
      );

      continue;
    }

    // -----------------------------------
    // RAW VALUE
    // example:
    // disabled=true
    // size=small
    // -----------------------------------

    let raw = "";

    while (
        i < str.length &&
        !/\s/.test(str[i])
        ) {
      raw += str[i++];
    }

    props[key] = raw;
  }

  return props;
}

function normalizeTemplate(input: string) {

  return input

      // tab -> 2 spaces
      .replace(/\t/g, "  ")

      // windows line ending
      .replace(/\r\n/g, "\n")

      // remove trailing spaces
      .replace(/[ \t]+$/gm, "");
}


export function parseTemplate( input: string, context: any = {} ) {

  // -----------------------------------
  // REMOVE COMMENTS
  // -----------------------------------
  input = normalizeTemplate(input);

  input = input.replace(
      /{#[\s\S]*?#}/g,
      ""
  );

  const stack: any[] = [];

  const root: any = { type: "root", children: [] };

  let current = root;

  let cursor = 0;

  // -----------------------------------
  // PARSE LOOP
  // -----------------------------------

  while (true) {

    const match = findNextTag( input, cursor );

    if (!match) break;

    // -----------------------------------
    // TEXT NODE
    // -----------------------------------

    const rawText = input.slice( cursor, match.start );

    if (rawText.trim()) {
      current.children.push({ type: "text", content: rawText });
    }

    // -----------------------------------
    // TAG CONTENT
    // -----------------------------------

    const tagContent = match.content
        .slice(2, -2)
        .trim();

    // -----------------------------------
    // SELF CLOSE
    // -----------------------------------

    const isSelfClose =
        /\/\s*$/.test(tagContent);

    // -----------------------------------
    // REMOVE /
    // -----------------------------------

    const clean = tagContent
        .replace(/\/\s*$/, "")
        .trim();

    // -----------------------------------
    // CLOSING TAG
    // -----------------------------------

    const isClosing =
        clean.startsWith("end_");

    // -----------------------------------
    // TAG NAME
    // -----------------------------------

    const whitespaceMatch =
        clean.match(/\s/);

    const spaceIndex =
        whitespaceMatch?.index ?? -1;

    const tagName =
        spaceIndex === -1
            ? clean
            : clean.slice(
                0,
                spaceIndex
            );

    const normalizedTagName =
        isClosing
            ? tagName.replace(
                /^end_/,
                ""
            )
            : tagName;

    // -----------------------------------
    // RAW PROPS
    // -----------------------------------

    const rawProps =
        spaceIndex === -1
            ? ""
            : clean.slice(
                spaceIndex + 1
            );

    // -----------------------------------
    // CLOSE TAG
    // -----------------------------------

    if (isClosing) {

      if (
          closingMatches(
              current?.type,
              normalizedTagName
          )
      ) {
        current =
            stack.pop() || root;
      } else {
        let matchIndex = -1;

        for (
            let index = stack.length - 1;
            index >= 0;
            index--
        ) {
          if (
              closingMatches(
                  stack[index]?.type,
                  normalizedTagName
              )
          ) {
            matchIndex = index;
            break;
          }
        }

        if (matchIndex !== -1) {
          current = stack[
              Math.max(matchIndex - 1, 0)
          ] || root;
          stack.length = matchIndex;
        }
      }

    }

        // -----------------------------------
        // OPEN TAG
    // -----------------------------------

    else {

      const node = {
        type: normalizedTagName,
        props: parseProps(
            rawProps,
            context
        ),
        children: [],
      };

      current.children.push(
          node
      );

      if (
          !isSelfClose &&
          !isSelfClosing(
              normalizedTagName
          )
      ) {

        stack.push(current);

        current = node;

      }
    }

    cursor = match.end;
  }

  // -----------------------------------
  // TAIL TEXT
  // -----------------------------------

  const tailText =
      input.slice(cursor);

  if (tailText.trim()) {

    current.children.push({
      type: "text",
      content: tailText,
    });

  }

  return root.children;
}

function closingMatches(
    currentType: string,
    closingType: string
) {
  if (currentType === closingType) {
    return true;
  }

  const boxGroupTypes = [
    "box_group",
    "box_radio_group",
    "box_checkbox_group",
  ];

  if (
      closingType === "box_group" &&
      boxGroupTypes.includes(currentType)
  ) {
    return true;
  }

  return false;
}

function findNextTag(input: string, start = 0) {
  const open = input.indexOf("{%", start);
  if (open === -1) return null;

  let i = open + 2;
  let braceDepth = 0;
  let parenDepth = 0;
  let bracketDepth = 0;
  let quote: string | null = null;

  while (i < input.length - 1) {
    const char = input[i];
    const next = input[i + 1];

    if (quote) {
      if (char === "\\" && i + 1 < input.length) { i += 2; continue; }
      if (char === quote) quote = null;
      i++;
      continue;
    }

    if (char === '"' || char === "'" || char === "`") { quote = char; i++; continue; }

    if (char === "{") { braceDepth++; i++; continue; }
    if (char === "}") {
      if (braceDepth > 0) { braceDepth--; i++; continue; }
      // braceDepth đã = 0: đây là } kết thúc expression ngoài cùng
      // KHÔNG giảm xuống âm, bỏ qua
      i++;
      continue;
    }

    if (char === "(") { parenDepth++; i++; continue; }
    if (char === ")") { if (parenDepth > 0) parenDepth--; i++; continue; }
    if (char === "[") { bracketDepth++; i++; continue; }
    if (char === "]") { if (bracketDepth > 0) bracketDepth--; i++; continue; }

    // Chỉ kết thúc tag khi TẤT CẢ depth = 0
    if (
        braceDepth === 0 && parenDepth === 0 && bracketDepth === 0 &&
        char === "%" && next === "}"
    ) {
      return {
        start: open,
        end: i + 2,
        content: input.slice(open, i + 2),
      };
    }

    i++;
  }

  return null;
}

function isSelfClosing(
    tag: string
) {

  return [

    "img",
    "checkbox",
    "switch",
    "radio",
    "select",

    "divider",

    "br",
    "hr",

    "include",
    "form_effect",
    "set",

  ].includes(tag);
}

export function safeEval(
    code: string,
    scope: any = {},
    key?: string
) {


  try {

    return Function(
        "__scope",
        `
with (__scope) {
  return (${code});
}
`
    )(
        scope
    );

  } catch (e) {
    if(key === 'condition')
    console.warn("Eval error:",  code );
    return { __expr: code };
  }
}
